/**
 * MHR Dashboard Page
 *
 * Main landing page for the Medical History Review tool.
 * Shows protocol list with search/filter, budget overview, and recent reviews.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  FolderOpen,
  ListChecks,
  AlertCircle,
  RefreshCw,
  ClipboardCheck,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import { useMHR } from '../contexts';
import { ProtocolCard } from '../components/protocol/ProtocolCard';
import { PageHeader } from '../../ui';
import * as mhrApi from '../lib/api';
import type { Protocol, ProtocolStatus, Review, ReviewStatus } from '../types';

// Review status badge component
function ReviewStatusBadge({ status }: { status: ReviewStatus }) {
  const statusConfig: Record<
    ReviewStatus,
    { label: string; bgColor: string; textColor: string; icon: typeof CheckCircle }
  > = {
    pending: { label: 'Pending', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-600 dark:text-gray-400', icon: Clock },
    budget_pending: {
      label: 'Budget Pending',
      bgColor: 'bg-amber-100 dark:bg-amber-900/40',
      textColor: 'text-amber-700 dark:text-amber-300',
      icon: Clock,
    },
    in_progress: {
      label: 'In Progress',
      bgColor: 'bg-blue-100 dark:bg-blue-900/40',
      textColor: 'text-blue-700 dark:text-blue-300',
      icon: Clock,
    },
    pass_1_exclusions: {
      label: 'Analyzing',
      bgColor: 'bg-blue-100 dark:bg-blue-900/40',
      textColor: 'text-blue-700 dark:text-blue-300',
      icon: Clock,
    },
    pass_2_inclusions: {
      label: 'Analyzing',
      bgColor: 'bg-blue-100 dark:bg-blue-900/40',
      textColor: 'text-blue-700 dark:text-blue-300',
      icon: Clock,
    },
    pass_3_verification: {
      label: 'Verifying',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/40',
      textColor: 'text-indigo-700 dark:text-indigo-300',
      icon: Clock,
    },
    completed: {
      label: 'Completed',
      bgColor: 'bg-green-100 dark:bg-green-900/40',
      textColor: 'text-green-700 dark:text-green-300',
      icon: CheckCircle,
    },
    failed: { label: 'Failed', bgColor: 'bg-red-100 dark:bg-red-900/40', textColor: 'text-red-700 dark:text-red-300', icon: XCircle },
    cancelled: {
      label: 'Cancelled',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
      textColor: 'text-gray-600 dark:text-gray-400',
      icon: XCircle,
    },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.textColor}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
}

// Recent review card component
function RecentReviewCard({ review, onClick }: { review: Review; onClick: () => void }) {
  const resultConfig: Record<string, { icon: typeof CheckCircle; color: string }> = {
    eligible: { icon: CheckCircle, color: 'text-green-600 dark:text-green-400' },
    not_eligible: { icon: XCircle, color: 'text-red-600 dark:text-red-400' },
    needs_review: { icon: HelpCircle, color: 'text-amber-600 dark:text-amber-400' },
  };

  const result = resultConfig[review.overall_result] || resultConfig.needs_review;
  const ResultIcon = result.icon;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all text-left"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              Patient {review.patient_id?.slice(0, 8)}...
            </span>
            <ReviewStatusBadge status={review.status} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(review.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
          {review.status === 'completed' && (
            <div className="flex items-center gap-2 mt-2">
              <ResultIcon size={14} className={result.color} />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {review.summary.met} met, {review.summary.not_met} not met,{' '}
                {review.summary.uncertain} uncertain
              </span>
            </div>
          )}
        </div>
        <ChevronRight size={18} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
      </div>
    </button>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { protocols, protocolsLoading, loadProtocols, error, clearError } = useMHR();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProtocolStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Budget state
  const [budgetUsage, setBudgetUsage] = useState<mhrApi.MonthlyUsage | null>(null);
  const [budgetConfig, setBudgetConfig] = useState<mhrApi.BudgetConfig | null>(null);

  // Recent reviews state
  const [recentReviews, setRecentReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Load budget data
  useEffect(() => {
    const loadBudget = async () => {
      try {
        const [usage, config] = await Promise.all([
          mhrApi.getMonthlyUsage(),
          mhrApi.getBudgetConfig(),
        ]);
        setBudgetUsage(usage);
        setBudgetConfig(config);
      } catch (err) {
        console.error('[MHR Dashboard] Error loading budget:', err);
      }
    };
    loadBudget();
  }, []);

  // Load recent reviews
  useEffect(() => {
    const loadRecentReviews = async () => {
      setReviewsLoading(true);
      try {
        const reviews = await mhrApi.listReviews({});
        // Sort by created_at descending and take top 5
        const sorted = reviews
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);
        setRecentReviews(sorted);
      } catch (err) {
        console.error('[MHR Dashboard] Error loading reviews:', err);
      } finally {
        setReviewsLoading(false);
      }
    };
    loadRecentReviews();
  }, []);

  // Filter protocols based on search and status
  const filteredProtocols = useMemo(() => {
    return protocols.filter(protocol => {
      // Status filter
      if (statusFilter !== 'all' && protocol.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          protocol.protocol_number.toLowerCase().includes(query) ||
          protocol.protocol_title?.toLowerCase().includes(query) ||
          protocol.sponsor_name?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [protocols, searchQuery, statusFilter]);

  // Budget usage percentage
  const budgetPercentUsed = useMemo(() => {
    if (!budgetUsage || !budgetConfig) return 0;
    return Math.min(100, (budgetUsage.total_cost / budgetConfig.monthly_budget) * 100);
  }, [budgetUsage, budgetConfig]);

  const handleRefresh = () => {
    loadProtocols(true);
  };

  return (
    <div className="space-y-6">
      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title="Medical History Review"
        icon={FolderOpen}
        subtitle="Manage protocols and analyze patient eligibility"
        actions={
          <Link
            to="/mhr/protocols/new"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            <Plus size={18} />
            New Protocol
          </Link>
        }
      />

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Protocols count */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
              <FolderOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Protocols</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {protocols.filter(p => p.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        {/* Criteria count */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <ListChecks className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Criteria</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {protocols.reduce((sum, p) => sum + (p.criteria_count || 0), 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Budget usage */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Budget</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              ${budgetUsage?.total_cost?.toFixed(2) || '0.00'} / $
              {budgetConfig?.monthly_budget?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                budgetPercentUsed > 80 ? 'bg-amber-500' : 'bg-primary-600'
              }`}
              style={{ width: `${budgetPercentUsed}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {budgetUsage?.reviews_completed || 0} reviews completed this month
          </p>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search protocols..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Filter size={18} />
            Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={protocolsLoading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            <RefreshCw size={18} className={protocolsLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filter options */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as ProtocolStatus | 'all')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Statuses</option>
              <option value="setup">Setup</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      )}

      {/* Recent Reviews Section */}
      {recentReviews.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Reviews</h2>
            </div>
          </div>
          {reviewsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentReviews.map(review => (
                <RecentReviewCard
                  key={review.review_id}
                  review={review}
                  onClick={() => navigate(`/mhr/reviews/${review.review_id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Protocol list */}
      {protocolsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredProtocols.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <FolderOpen className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No protocols found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first protocol'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link
              to="/mhr/protocols/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] sm:min-h-0"
            >
              <Plus size={20} />
              Create Protocol
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProtocols.map(protocol => (
            <ProtocolCard
              key={protocol.id}
              protocol={protocol}
              onClick={() => navigate(`/mhr/protocols/${protocol.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
